import { makeObservable, observable } from 'mobx'
import { type IActionsProps } from 'shared/ui'
import type {
  IResponseAttachmentUpload,
  IResponseMedia,
  IResponseMediaShort,
} from 'entities/Attachment'
import { getAttachmentType } from 'entities/Attachment/lib/getAttachmentType'
import { type IParsedVcard } from 'entities/VCard'
import { VCard } from 'entities/VCard/model/VCard'
import { type IResponseDefaultAudio } from 'entities/Call'
import { type IResponseVoiceGreeting } from 'entities/Greetings/api/types.ts'
import { type RequestAttachment } from './RequestAttachment'

type IAttachmentType =
  | 'image'
  | 'gif'
  | 'video'
  | 'audio'
  | 'previewGif'
  | 'attachment'
  | 'voice'
  | 'vcard'
  | 'pdf'

export class Attachment {
  id: number | string = ''
  media_preview?: Attachment
  content_type = ''
  source?: string
  thumbnail?: string | null = null
  extension?: string | null = null
  source_short?: string
  original_source?: string
  created_at?: string
  duration?: number
  name?: string | null
  size?: number | null
  width: number | null = null
  height: number | null = null
  is_allowed_for_media_url?: boolean
  parsedVCard: Array<IParsedVcard> = []
  type: IAttachmentType = 'attachment'
  vCard: VCard | null = null
  file?: File | null = null
  searchFragments: string[] = []
  userId: number | null = null
  loading = false
  actionsProps: IActionsProps | null = null

  readonly responseDefaultAudio: IResponseDefaultAudio | null = null

  constructor({
    responseMedia,
    responseUpload,
    requestAttachment,
    file,
    imageUrl,
    pdfUrl,
    id,
    fromMediaUrl,
    isSave,
    responseMediaShort,
    responseVoiceGreeting,
    responseDefaultAudio,
    name,
    loading = false,
    isGenerateExtensionName,
  }: {
    responseMedia?: IResponseMedia
    responseMediaShort?: IResponseMediaShort
    responseUpload?: IResponseAttachmentUpload
    responseVoiceGreeting?: IResponseVoiceGreeting
    responseDefaultAudio?: IResponseDefaultAudio
    requestAttachment?: RequestAttachment
    file?: File
    imageUrl?: string
    pdfUrl?: string
    id?: number | string
    fromMediaUrl?: boolean
    isSave?: boolean
    name?: string
    loading?: boolean
    isGenerateExtensionName?: boolean
  }) {
    this.loading = loading

    if (requestAttachment) {
      this.id = requestAttachment.url
      this.source = requestAttachment.url
      this.name = requestAttachment.name || requestAttachment.url.split('/').pop()
      this.width = requestAttachment.width
      this.height = requestAttachment.height
    }
    if (responseVoiceGreeting) {
      this.id = responseVoiceGreeting.id
      this.source = responseVoiceGreeting.url
      this.name = responseVoiceGreeting.name || responseVoiceGreeting.url.split('/').pop()
      this.created_at = responseVoiceGreeting.created_at
      this.type = getAttachmentType({
        content_type: responseVoiceGreeting.content_type,
        name: this.name || '',
      })
      this.content_type = responseVoiceGreeting.content_type
      this.duration = responseVoiceGreeting.duration
    }
    if (responseDefaultAudio) {
      this.responseDefaultAudio = responseDefaultAudio
      this.id = responseDefaultAudio.url
      this.source = responseDefaultAudio.url
      this.name = responseDefaultAudio.name
      this.type = getAttachmentType({
        content_type: responseDefaultAudio.content_type,
        name: this.name || '',
      })
      this.content_type = responseDefaultAudio.content_type
    }
    if (responseMediaShort) {
      this.id = responseMediaShort.url || ''
      this.name = responseMediaShort.name || responseMediaShort.source?.split('/').pop()
      this.source = responseMediaShort.source || responseMediaShort.url
      this.is_allowed_for_media_url =
        responseMediaShort.is_allowed_for_media_url !== undefined
          ? responseMediaShort.is_allowed_for_media_url
          : true
      this.source_short = responseMediaShort.source_short
      if (responseMediaShort.original_source)
        this.original_source = responseMediaShort.original_source
      this.content_type = responseMediaShort.content_type || ''
      this.type = getAttachmentType({
        content_type: responseMediaShort.content_type || '',
        name: this.name || '',
      })
      this.duration = responseMediaShort.duration
      if (responseMediaShort.size !== null && responseMediaShort.size !== undefined)
        this.size = Number(responseMediaShort.size)
      this.width = responseMediaShort.width ?? null
      this.height = responseMediaShort.height ?? null
    }
    if (responseMedia) {
      this.id = responseMedia.id
      this.thumbnail = responseMedia.thumbnail
      this.extension = responseMedia.extension
      this.created_at = responseMedia.created_at
      this.duration = responseMedia.duration
      this.source = responseMedia.source
      this.name = responseMedia.name || responseMedia.source.split('/').pop()
      this.loading = Boolean(responseMedia.processing)
      this.size = Number(responseMedia.size)
      this.userId = Number(responseMedia.user_id)
      this.width = responseMedia.width
      this.height = responseMedia.height
    }
    if (responseUpload) {
      this.thumbnail = responseUpload.thumbnail
      this.extension = responseUpload.extension
      this.id = responseUpload.id || responseUpload.source
      this.duration = responseUpload.duration
      this.source = responseUpload.source
      this.name = responseUpload.name || responseUpload.source.split('/').pop()
      this.size = Number(responseUpload.size)
      this.width = responseUpload.width
      this.height = responseUpload.height
    }

    if (file) {
      const url = URL.createObjectURL(file)
      this.file = file
      this.id = url
      this.source = url
      this.name = file.name
      this.type = getAttachmentType({
        content_type: file.type,
        name: this.name || '',
      })
    }
    if (imageUrl) {
      this.id = id || imageUrl
      this.source = imageUrl
      this.type = 'image'
      this.is_allowed_for_media_url = fromMediaUrl
      this.name = name || imageUrl.split('/').pop()
    }
    if (pdfUrl) {
      this.id = id || pdfUrl
      this.source = pdfUrl
      this.type = 'pdf'
      this.name = name || pdfUrl.split('/').pop()
    }

    const media = requestAttachment || responseMedia || responseUpload
    if (media) {
      this.source = this.source || media.source
      this.original_source = media.original_source
      this.source_short = media.source_short
      this.is_allowed_for_media_url = fromMediaUrl || !!media.is_allowed_for_media_url
      this.media_preview =
        media.media_preview &&
        new Attachment({
          responseMedia: {
            ...media.media_preview,
            is_allowed_for_media_url: true,
          },
        })

      this.content_type = media.content_type
      this.parsedVCard = this.getParsedVCard(media.parsedVCard)
      this.type = getAttachmentType({
        content_type: media.content_type,
        name: this.name || '',
        original_source: this.original_source,
        parsedVCard: this.parsedVCard,
      })
      this.vCard =
        (this.parsedVCard[0] &&
          new VCard({
            ...this.parsedVCard[0],
            isSave,
            fileName: media.name,
            source: media.source,
          })) ||
        null
    }

    this.searchFragments = [
      this.name,
      this.vCard?.first_name,
      this.vCard?.last_name,
      this.vCard?.email,
      this.vCard?.phone,
    ].reduce<string[]>(
      (state, fragment) => (typeof fragment === 'string' ? [...state, fragment] : state),
      []
    )

    if (isGenerateExtensionName) {
      this._generateExtensionName()
    }
    makeObservable(this, {
      loading: observable,
      name: observable,
    })
  }

  getParsedVCard(parsedVCard?: Array<IParsedVcard> | IParsedVcard): Array<IParsedVcard> {
    if (parsedVCard) {
      if (Array.isArray(parsedVCard)) {
        return parsedVCard
      } else {
        return [parsedVCard]
      }
    }
    return []
  }

  setLoading = (status: boolean) => {
    this.loading = status
  }

  setActionsProps: (actionProps: IActionsProps) => void = (actionProps: IActionsProps) => {
    this.actionsProps = actionProps
  }

  get isVcard() {
    return this.type === 'vcard'
  }

  get isPreviewGif() {
    return !!(this.content_type === 'image/gif' && this.source_short && this.original_source)
  }
  private _generateExtensionName = () => {
    const split = this.name?.split('.')
    const currentExtName = split && split.length > 1 && split.pop()
    if (currentExtName) return
    const sourceFileName = this.source?.split('/').pop()
    if (sourceFileName) {
      const sourceExt = sourceFileName.split('.').pop()
      if (sourceExt) {
        this.name += `.${sourceExt}`
      }
    }
  }

  setName = (name: string) => {
    this.name = name
  }
}
